import * as React from "react";
import { Clock } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function label12(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

const SIZE = 200;
const CENTER = SIZE / 2;
const RADIUS = 80;

function positions(count: number, step: number) {
  return Array.from({ length: count }, (_, i) => {
    const n = i * step;
    const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
    return { n, x: CENTER + RADIUS * Math.cos(angle), y: CENTER + RADIUS * Math.sin(angle) };
  });
}
const HOURS = positions(12, 1).map((p, i) => ({ ...p, n: i === 0 ? 12 : i })); // 12 at top, then 1..11
const MINUTES = positions(12, 5); // 0,5,…,55

/**
 * An analog-clock time picker: click the hour on the dial, then the minute, with
 * an AM/PM toggle. Value is 24-hour {@code HH:MM}; the trigger shows a 12-hour label.
 */
export function ClockTimePicker({
  value,
  onChange,
  id,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  id?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<"hours" | "minutes">("hours");
  const [h, m] = value.split(":").map(Number);
  const ampm: "AM" | "PM" = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;

  const emit = (hh: number, mm: number) =>
    onChange(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);

  const pickHour = (hh12: number) => {
    const base = ampm === "PM" ? (hh12 % 12) + 12 : hh12 % 12;
    emit(base, m);
    setMode("minutes");
  };
  const pickMinute = (mm: number) => {
    emit(h, mm);
    setOpen(false);
    setMode("hours");
  };
  const setAmpm = (ap: "AM" | "PM") => {
    if (ap === ampm) return;
    emit(ap === "PM" ? (h % 12) + 12 : h % 12, m);
  };

  const points = mode === "hours" ? HOURS : MINUTES;
  const activeN = mode === "hours" ? hour12 : m;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setMode("hours");
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          aria-label={ariaLabel}
          className="flex h-10 items-center gap-2 rounded-md border-2 border-black bg-background px-3 text-sm"
        >
          <Clock className="h-4 w-4 text-muted-foreground" />
          {label12(value)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-md border-2 border-black p-3">
        <div className="mb-2 flex items-center justify-center gap-2 font-comic-headline text-xl">
          <button
            type="button"
            className={cn("tabular-nums", mode === "hours" && "text-teal-700 underline")}
            onClick={() => setMode("hours")}
          >
            {String(hour12).padStart(2, "0")}
          </button>
          <span>:</span>
          <button
            type="button"
            className={cn("tabular-nums", mode === "minutes" && "text-teal-700 underline")}
            onClick={() => setMode("minutes")}
          >
            {String(m).padStart(2, "0")}
          </button>
          <div className="ml-2 flex flex-col text-xs">
            <button
              type="button"
              onClick={() => setAmpm("AM")}
              className={ampm === "AM" ? "font-bold text-teal-700" : "text-muted-foreground"}
            >
              AM
            </button>
            <button
              type="button"
              onClick={() => setAmpm("PM")}
              className={ampm === "PM" ? "font-bold text-teal-700" : "text-muted-foreground"}
            >
              PM
            </button>
          </div>
        </div>

        <div
          className="relative rounded-full border-2 border-black bg-yellow-50 dark:bg-background"
          style={{ width: SIZE, height: SIZE }}
        >
          {points.map((p) => {
            const active = p.n === activeN;
            return (
              <button
                key={p.n}
                type="button"
                onClick={() => (mode === "hours" ? pickHour(p.n) : pickMinute(p.n))}
                style={{ left: p.x, top: p.y, transform: "translate(-50%, -50%)" }}
                className={cn(
                  "absolute flex h-8 w-8 items-center justify-center rounded-full text-sm tabular-nums transition",
                  active ? "bg-teal-600 font-semibold text-white" : "hover:bg-teal-100",
                )}
              >
                {mode === "hours" ? p.n : String(p.n).padStart(2, "0")}
              </button>
            );
          })}
          <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
        </div>
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          {mode === "hours" ? "Pick the hour" : "Pick the minutes"}
        </p>
      </PopoverContent>
    </Popover>
  );
}
